import React, { useEffect, useRef, useMemo, useState } from 'react';

import {
    Container,
    Row,
    Col,
    Heading,
    className,
    Slider,
    isMobile,
} from 'design-system';
import { useTranslate } from 'gatsby-theme-soluble-source';

import { removeDynamicsStyle, getScript } from '../../utils';
import TransitionLink from '../TransitionLink';
import HtmlComponent from '../HtmlComponent';
import CardPerson from '../CardPerson';

import style from './styles.module.scss';

function SidebarLinksList({ title, links }) {
    return (
        <div {...className('mb-5')}>
            <Heading
                tag="h5"
                className={`body font-weight-bold mb-2 text-uppercase`}>
                {title}
            </Heading>
            {links.map((related, i) => {
                return (
                    <TransitionLink
                        key={i}
                        to={related.slug}
                        title={related.title}
                        {...className(
                            `body ${
                                related.isBusinessUnit
                                    ? `${style['sidebar-business-link']} mb-2 mt-2`
                                    : `${style['sidebar-link']} mb-1`
                            }`,
                        )}>
                        {related.title}
                    </TransitionLink>
                );
            })}
        </div>
    );
}
const sliderSettings = {
    controls: false,
    loop: false,
    nav: false,
    items: 1,
    gutter: 20,
    navAsThumbnails: true,
    mouseDrag: true,
    edgePadding: 50,
};
export default function PostContentSection({
    content,
    // sectors,
    services,
    categories,
    teamMembers,
}) {
    const translation = useTranslate();
    const contentRef = useRef();
    const sliderRef = useRef();
    const [isFirst, setIsFirst] = useState(true);
    const [isLast, setIsLast] = useState(false);
    const [isMobileView, setIsMobileView] = useState(isMobile());
    const scripts = useMemo(() => {
        let scripts = [];

        if (content && typeof content !== 'string' && content.blocks?.length) {
            content.blocks.forEach((block) => {
                if (
                    block.__typename === 'DatoCmsSnippet' &&
                    block.contentScripts?.length
                ) {
                    scripts = [...scripts, ...block.contentScripts];
                }
            });
        }

        return scripts;
    }, [content]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !scripts ||
            !scripts.length ||
            !contentRef ||
            !contentRef.current
        ) {
            return;
        }

        scripts.forEach((script, i) => {
            let newScript = getScript(script);
            setTimeout(() => {
                try {
                    contentRef.current.appendChild(newScript);
                } catch (e) {}
                // TODO: Fix this ugly way to avoid conflicts between dependen scripts. script.onload?
            }, i * 2000);
        });

        return () => {
            window.MsCrmMkt = null;
        };
    }, [contentRef, scripts]);

    useEffect(() => {
        if (!contentRef?.current || typeof window === 'undefined') {
            return;
        }

        let timeout = removeDynamicsStyle(contentRef.current);

        return () => {
            if (timeout) {
                window.clearTimeout(timeout);
                timeout = null;
            }
        };
    }, [contentRef]);
    useEffect(() => {
        const configureClasses = (slider) => {
            setIsFirst(slider.index === 0 ? true : false);
            setIsLast(slider.index + 1 === slider.slideCount ? true : false);
        };

        if (sliderRef.current) {
            sliderRef.current.slider.events.on(
                'indexChanged',
                configureClasses,
            );
        }

        return () => {
            if (sliderRef.current?.slider) {
                sliderRef.current.slider.events.off(
                    'indexChanged',
                    configureClasses,
                );
            }
        };
    }, [setIsFirst, setIsLast, sliderRef]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const onResizeListener = () => {
            setIsMobileView(isMobile());
        };

        window.addEventListener('resize', onResizeListener);
        return () => {
            window.removeEventListener('resize', onResizeListener);
        };
    }, [setIsMobileView]);
    return (
        <section>
            <Container pt={{ xs: 10, lg: 5 }} pb={{ xs: 20, lg: 30 }}>
                <Row className={`${style['container']}`}>
                    <Col offset={{ lg: 1 }} col={{ lg: 6, xl: 7 }}>
                        <HtmlComponent ref={contentRef} content={content} />
                    </Col>
                    <Col offset={{ lg: 1 }} col={{ lg: 4, xl: 3 }}>
                        {/*sectors?.length ? (
                            <SidebarLinksList
                                title={translation[`post.related.sectors`].text}
                                links={sectors}
                            />
                        ) : null*/}
                        {services?.length ? (
                            <SidebarLinksList
                                title={
                                    translation[`post.related.services`].text
                                }
                                links={services}
                            />
                        ) : null}
                        {categories?.length ? (
                            <SidebarLinksList
                                title={
                                    translation[`post.related.categories`].text
                                }
                                links={categories}
                            />
                        ) : null}
                        {teamMembers?.length ? (
                            <>
                                {teamMembers.length === 1 || !isMobileView ? (
                                    <div {...className('mt-10')}>
                                        {teamMembers.map((tm, i) => (
                                            <CardPerson key={i} {...tm} />
                                        ))}
                                    </div>
                                ) : (
                                    <div
                                        {...className(
                                            `mt-10 ${style.slider} ${
                                                isFirst
                                                    ? style['slider--in-first']
                                                    : ''
                                            } ${
                                                isLast
                                                    ? style['slider--in-last']
                                                    : ''
                                            }`,
                                        )}>
                                        <span>
                                            <Slider
                                                settings={sliderSettings}
                                                parentRef={sliderRef}>
                                                {teamMembers.map((tm, i) => (
                                                    <div
                                                        className={
                                                            style[
                                                                'slider__slide'
                                                            ]
                                                        }>
                                                        <CardPerson
                                                            key={i}
                                                            {...tm}
                                                        />
                                                    </div>
                                                ))}
                                            </Slider>
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
